// src/pages/Programs.tsx
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Project } from '../types';
import ProjectCard from '../components/UI/ProjectCard';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { apiService } from '../services/api';

const Programs: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [viewMode, setViewMode] = useState<'thematic' | 'projects'>('thematic');

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await apiService.getProjects();
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const thematicAreas = [
    { 
      key: 'food_security', 
      label: 'Food Security & Nutrition', 
      icon: '🌱',
      color: 'from-gray-500 to-gray-700',
      stats: '1K+ Families Reached',
      image: '/images/food-security-thematic.jpg',
      objectives: [
        'Promote sustainable agricultural practices',
        'Improve access to nutritious food',
        'Support smallholder farmers',
        'Reduce food waste and losses'
      ]
    },
    { 
      key: 'climate_action', 
      label: 'Climate Action & Resilience', 
      icon: '🌍',
      color: 'from-gray-600 to-gray-800',
      stats: '10K+ Trees Planted',
      image: '/images/climate-action-thematic.jpg',
      objectives: [
        'Promote reforestation and conservation',
        'Build climate-resilient communities',
        'Reduce carbon footprint',
        'Support renewable energy adoption'
      ]
    },
    { 
      key: 'youth_empowerment', 
      label: 'Youth Empowerment', 
      icon: '👨‍🎓',
      color: 'from-gray-400 to-gray-600',
      stats: '5K+ Youth Trained',
      image: '/images/youth-empowerment-thematic.jpg',
      objectives: [
        'Provide skills development training',
        'Create green job opportunities',
        'Foster youth leadership',
        'Support youth-led initiatives'
      ]
    },
    { 
      key: 'women_empowerment', 
      label: 'Women Empowerment', 
      icon: '👩‍🌾',
      color: 'from-gray-500 to-gray-700',
      stats: '2K+ Women Empowered',
      image: '/images/women-empowerment-thematic.jpg',
      objectives: [
        'Support women entrepreneurs',
        'Provide leadership training',
        'Ensure gender equality',
        'Promote women in agriculture'
      ]
    }
  ];

  // Create sample projects if API returns empty
  const sampleProjects: Project[] = [
    {
      id: 1,
      title: 'Green Skills Project',
      slug: 'green-skills-project',
      description: 'Inspiring youth to promote sustainable agriculture and environmental awareness through hands-on training in kitchen gardening.',
      full_content: 'Full content about Green Skills Project...',
      category: 'youth_empowerment',
      image: '/images/green-skills-project.jpg',
      goals: 'Empower youth with agricultural skills',
      progress: 100,
      status: 'active',
      created_at: '2024-01-15',
      impact: '500+ students trained',
      location: 'West Pokot County',
      duration: 'Ongoing',
      featured: true
    },
    {
      id: 2,
      title: 'Women Farming Collective',
      slug: 'women-farming-collective',
      description: 'Empowering women farmers with skills and resources for sustainable agriculture and market access.',
      full_content: 'Full content about Women Farming Collective...',
      category: 'women_empowerment',
      image: '/images/women-farming.jpg',
      goals: 'Support women in agriculture',
      progress: 100,
      status: 'active',
      created_at: '2024-02-01',
      impact: '200+ women supported',
      location: 'Rural Communities',
      duration: '2 months',
      featured: true
    },
    {
      id: 3,
      title: 'Climate Resilience Program',
      slug: 'climate-resilience-program',
      description: 'Building community resilience to climate change through sustainable practices and education.',
      full_content: 'Full content about Climate Resilience Program...',
      category: 'climate_action',
      image: '/images/climate-action.jpg',
      goals: 'Build climate resilience',
      progress: 100,
      status: 'active',
      created_at: '2024-01-20',
      impact: '25K+ trees planted',
      location: 'Multiple Counties',
      duration: '2 Months',
      featured: true
    },
    {
      id: 4,
      title: 'Food Security Initiative',
      slug: 'food-security-initiative',
      description: 'Promoting sustainable food systems and nutrition security in vulnerable communities.',
      full_content: 'Full content about Food Security Initiative...',
      category: 'food_security',
      image: '/images/food-security.jpg',
      goals: 'Ensure food security',
      progress: 100,
      status: 'active',
      created_at: '2024-01-10',
      impact: '1K+ families reached',
      location: 'Various Locations',
      duration: 'Ongoing',
      featured: true
    },
    {
      id: 5,
      title: 'Youth Leadership Program',
      slug: 'youth-leadership-program',
      description: 'Developing young leaders in sustainable agriculture and community development.',
      full_content: 'Full content about Youth Leadership Program...',
      category: 'youth_empowerment',
      image: '/images/youth-leadership.jpg',
      goals: 'Develop youth leaders',
      progress: 100,
      status: 'active',
      created_at: '2024-02-15',
      impact: '1K+ youth leaders trained',
      location: 'Urban Centers',
      duration: '1 Year',
      featured: false
    },
    {
      id: 6,
      title: 'Women Entrepreneurship',
      slug: 'women-entrepreneurship',
      description: 'Supporting women entrepreneurs in agribusiness and sustainable ventures.',
      full_content: 'Full content about Women Entrepreneurship...',
      category: 'women_empowerment',
      image: '/images/women-entrepreneurship.jpg',
      goals: 'Support women entrepreneurs',
      progress: 100,
      status: 'active',
      created_at: '2024-01-25',
      impact: '500+ businesses started',
      location: 'Various Locations in West Pokot',
      duration: '2 Monthss',
      featured: false
    }
  ];

  // Use sample projects if API returns empty
  const displayProjects = projects.length > 0 ? projects : sampleProjects;

  const featuredProjects = displayProjects.filter(project => project.featured).slice(0, 3);
  const otherProjects = displayProjects.filter(project => !project.featured);

  const filteredProjects = activeCategory === 'all' 
    ? displayProjects 
    : displayProjects.filter(project => project.category === activeCategory);

  const getThematicAreaDescription = (category: string) => {
    const descriptions: { [key: string]: { 
      title: string; 
      description: string; 
      impact: string;
      approach: string;
      focusAreas: string[];
    } } = {
      food_security: {
        title: "Sustainable Food Systems & Nutrition",
        description: "We believe every African deserves dignified access to safe, nutritious, and sufficient food. Our food security programs work alongside local communities to promote productive food systems that respect our land, traditions, and indigenous knowledge while ensuring cultural relevance and nutrition.",
        impact: "15,000+ Families Achieving Food Security",
        approach: "Community-led solutions, sustainable agriculture, and nutrition education",
        focusAreas: [
          "Sustainable farming practices",
          "Nutrition education programs",
          "Food sovereignty initiatives",
          "Market access for smallholders"
        ]
      },
      climate_action: {
        title: "Climate Resilience & Environmental Action",
        description: "In the face of climate change, we empower communities with climate-smart solutions rooted in both indigenous knowledge and modern innovation. Our programs focus on building adaptive capacity and resilience to climate shocks while protecting natural resources.",
        impact: "25,000+ Trees Planted & 100+ Hectares Restored",
        approach: "Climate-smart agriculture, reforestation, and community adaptation",
        focusAreas: [
          "Reforestation and conservation",
          "Renewable energy adoption",
          "Climate education and awareness",
          "Sustainable land management"
        ]
      },
      youth_empowerment: {
        title: "Youth Leadership & Skills Development",
        description: "We believe youth are not only the stewards of tomorrow but the changemakers of today. Our youth empowerment programs equip young people with practical skills, indigenous knowledge, and innovative tools to actively participate in reshaping food systems and safeguarding natural resources.",
        impact: "5,000+ Youth Trained & 500+ Green Jobs Created",
        approach: "Skills training, leadership development, and entrepreneurship support",
        focusAreas: [
          "Green skills development",
          "Leadership training",
          "Entrepreneurship support",
          "Climate advocacy"
        ]
      },
      women_empowerment: {
        title: "Women-Led Development & Empowerment",
        description: "We honor the central role that women play as guardians of food security and nurturers of healthy communities. Our women empowerment programs provide training, resources, and support systems for women to become leaders in sustainable agriculture and community development.",
        impact: "2,000+ Women Entrepreneurs & Leaders",
        approach: "Gender equality, economic empowerment, and leadership development",
        focusAreas: [
          "Women in agriculture",
          "Leadership development",
          "Economic empowerment",
          "Gender equality advocacy"
        ]
      }
    };
    return descriptions[category] || descriptions.food_security;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Our Programs & Thematic Areas - Impact Generation Africa</title>
        <meta 
          name="description" 
          content="Explore Impact Generation Africa's thematic areas in food security, climate action, youth empowerment, and women empowerment. Discover our projects and initiatives creating sustainable change across African communities." 
        />
        <meta name="keywords" content="thematic areas, sustainable development, food security, climate action, youth empowerment, women empowerment, Africa programs" />
      </Helmet>

      {/* Enhanced Hero Section */}
      <section 
        className="min-vh-100 d-flex align-items-center justify-content-center position-relative overflow-hidden"
        style={{ 
          background: 'linear-gradient(135deg, rgba(74, 85, 104, 0.9) 0%, rgba(45, 55, 72, 0.95) 100%), url("/images/programs-hero.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          marginTop: '76px'
        }}
      >
        <div className="position-absolute top-0 start-0 w-100 h-100">
          <motion.div
            animate={{
              y: [0, -30, 0],
              rotate: [0, 180, 360]
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
            className="position-absolute"
            style={{
              top: '20%',
              left: '10%',
              fontSize: '6rem',
              opacity: 0.1
            }}
          >
            🌱
          </motion.div>
          <motion.div
            animate={{
              y: [0, 30, 0],
              rotate: [0, -180, -360]
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "linear"
            }}
            className="position-absolute"
            style={{
              bottom: '20%',
              right: '10%',
              fontSize: '4rem',
              opacity: 0.1
            }}
          >
            🌍
          </motion.div>
        </div>

        <div className="container position-relative z-2">
          <motion.div 
            className="row justify-content-center text-center text-white"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="col-lg-10">
              <motion.div
                variants={itemVariants}
                className="d-inline-flex align-items-center px-4 py-2 rounded-pill bg-white bg-opacity-10 backdrop-blur mb-4"
              >
                <span className="fw-semibold">🌟 Transforming Communities Since 2018</span>
              </motion.div>
              
              <motion.h1 
                variants={itemVariants}
                className="display-3 fw-black mb-4"
                style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                Our Thematic Areas & Programs
              </motion.h1>
              
              <motion.p 
                variants={itemVariants}
                className="lead fs-3 fw-light opacity-90 mb-5 mx-auto"
                style={{ maxWidth: '600px', lineHeight: '1.6' }}
              >
                Comprehensive initiatives across four key thematic areas, 
                driving sustainable development and empowerment in African communities
              </motion.p>

              {/* View Mode Toggle */}
              <motion.div
                variants={itemVariants}
                className="d-inline-flex bg-white bg-opacity-10 rounded-pill p-2 backdrop-blur"
              >
                <button
                  className={`btn ${viewMode === 'thematic' ? 'btn-primary' : 'btn-outline-light'} rounded-pill px-4 me-2`}
                  onClick={() => setViewMode('thematic')}
                >
                  🎯 Thematic Areas
                </button>
                <button
                  className={`btn ${viewMode === 'projects' ? 'btn-primary' : 'btn-outline-light'} rounded-pill px-4`}
                  onClick={() => setViewMode('projects')}
                >
                  📋 All Projects
                </button>
              </motion.div>

              {/* Quick Stats */}
              <motion.div 
                variants={containerVariants}
                className="row g-4 mt-5"
              >
                {[
                  { value: '50K+', label: 'Lives Impacted' },
                  { value: '100+', label: 'Communities' },
                  { value: '25K+', label: 'Trees Planted' },
                  { value: '5K+', label: 'Youth Trained' }
                ].map((stat) => (
                  <motion.div
                    key={stat.label}
                    className="col-6 col-md-3"
                    variants={itemVariants}
                    whileHover={{ scale: 1.05 }}
                  >
                    <div className="text-center">
                      <div className="fw-black display-6 mb-1">{stat.value}</div>
                      <div className="fw-light opacity-80">{stat.label}</div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          className="position-absolute bottom-0 start-50 translate-middle-x mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 2 }}
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            style={{
              width: '30px',
              height: '50px',
              border: '2px solid rgba(255,255,255,0.5)',
              borderRadius: '25px',
              display: 'flex',
              justifyContent: 'center',
              paddingTop: '10px'
            }}
          >
            <motion.div
              animate={{ y: [0, 15, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              style={{
                width: '4px',
                height: '10px',
                backgroundColor: 'rgba(255,255,255,0.8)',
                borderRadius: '2px'
              }}
            />
          </motion.div>
        </motion.div>
      </section>

      {/* Thematic Areas Section */}
      {viewMode === 'thematic' && (
        <section className="py-5 bg-gray-50">
          <div className="container">
            <motion.div 
              className="row mb-5"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <div className="col-12 text-center">
                <motion.h2 
                  variants={itemVariants}
                  className="display-4 fw-black text-gray-800 mb-4"
                >
                  Our Thematic Focus Areas
                </motion.h2>
                <motion.p 
                  variants={itemVariants}
                  className="lead text-gray-600 fs-5 mx-auto"
                  style={{ maxWidth: '700px' }}
                >
                  Four interconnected pillars that guide our work in creating sustainable impact 
                  and empowering communities across Africa
                </motion.p>
              </div>
            </motion.div>

            {/* Thematic Areas Grid */}
            <div className="row g-4">
              {thematicAreas.map((thematic, index) => (
                <motion.div
                  key={thematic.key}
                  className="col-12 col-md-6"
                  variants={itemVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <motion.div
                    className="card border-0 shadow-sm h-100 overflow-hidden"
                    whileHover={{ y: -5 }}
                    style={{ borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)' }}
                  >
                    <div className="row g-0 h-100">
                      {/* Image Column */}
                      <div className="col-md-5 position-relative">
                        <img 
                          src={thematic.image} 
                          alt={thematic.label}
                          className="w-100 h-100 object-fit-cover"
                          style={{ minHeight: '250px' }}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            target.nextElementSibling?.classList.remove('d-none');
                          }}
                        />
                        <div 
                          className={`position-absolute top-0 start-0 w-100 h-100 d-none ${thematic.color} d-flex align-items-center justify-content-center`}
                        >
                          <span className="display-1 text-white">{thematic.icon}</span>
                        </div>
                      </div>
                      
                      {/* Content Column */}
                      <div className="col-md-7">
                        <div className="card-body p-4 d-flex flex-column h-100">
                          <h3 className="card-title fw-bold text-gray-800 mb-3">
                            {thematic.label}
                          </h3>
                          
                          <div className="mb-3">
                            <span className="badge bg-gray-100 text-gray-700 fw-semibold border">
                              {thematic.stats}
                            </span>
                          </div>

                          <ul className="list-unstyled mb-4 flex-grow-1">
                            {thematic.objectives.map((objective, idx) => (
                              <li key={idx} className="mb-2 d-flex align-items-start">
                                <span className="text-gray-600 me-2">•</span>
                                <span className="text-gray-600">{objective}</span>
                              </li>
                            ))}
                          </ul>

                          <div className="mt-auto">
                            <button
                              className="btn btn-outline-gray-700 w-100 border-gray-300 text-gray-700"
                              onClick={() => {
                                setActiveCategory(thematic.key);
                                setViewMode('projects');
                              }}
                            >
                              Explore Projects →
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Enhanced Featured Projects Section */}
      {viewMode === 'thematic' && featuredProjects.length > 0 && (
        <section className="py-5 bg-white">
          <div className="container">
            <motion.div 
              className="row mb-5"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <div className="col-12 text-center">
                <motion.h2 
                  variants={itemVariants}
                  className="display-4 fw-black text-gray-800 mb-3"
                >
                  Featured Projects
                </motion.h2>
                <motion.p 
                  variants={itemVariants}
                  className="lead text-gray-600 fs-5 mb-4"
                >
                  Highlighted initiatives making significant impact across our thematic areas
                </motion.p>
                
                {/* Featured Projects Stats */}
                <motion.div
                  variants={itemVariants}
                  className="d-flex justify-content-center gap-4 flex-wrap mb-5"
                >
                  <div className="text-center">
                    <div className="fw-bold text-gray-800 fs-3">{featuredProjects.length}</div>
                    <div className="text-gray-600 small">Featured Projects</div>
                  </div>
                  <div className="text-center">
                    <div className="fw-bold text-gray-800 fs-3">{displayProjects.length}</div>
                    <div className="text-gray-600 small">Total Projects</div>
                  </div>
                  <div className="text-center">
                    <div className="fw-bold text-gray-800 fs-3">{thematicAreas.length}</div>
                    <div className="text-gray-600 small">Thematic Areas</div>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* Featured Projects Grid */}
            <div className="row g-4 mb-5">
              {featuredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  className="col-12 col-md-6 col-lg-4"
                  variants={itemVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <ProjectCard 
                    project={project} 
                    featured={true}
                    showDetails={true}
                  />
                </motion.div>
              ))}
            </div>

            {/* Additional Projects Preview */}
            {otherProjects.length > 0 && (
              <motion.div 
                className="row"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
              >
                <div className="col-12 text-center">
                  <motion.div
                    variants={itemVariants}
                    className="bg-gray-50 rounded-4 p-5 border"
                  >
                    <h3 className="fw-bold text-gray-800 mb-3">
                      Discover More Projects
                    </h3>
                    <p className="text-gray-600 mb-4">
                      We have {otherProjects.length} additional projects creating impact across various communities. 
                      Explore all our initiatives to see the full scope of our work.
                    </p>
                    <motion.button
                      className="btn btn-primary btn-lg px-5"
                      onClick={() => setViewMode('projects')}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      View All Projects ({displayProjects.length})
                    </motion.button>
                  </motion.div>
                </div>
              </motion.div>
            )}
          </div>
        </section>
      )}

      {/* All Projects View */}
      {viewMode === 'projects' && (
        <>
          {/* Category Filter */}
          <section className="py-5 bg-gray-50">
            <div className="container">
              <motion.div 
                className="row mb-4"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
              >
                <div className="col-12 text-center">
                  <motion.h2 
                    variants={itemVariants}
                    className="display-4 fw-black text-gray-800 mb-4"
                  >
                    {activeCategory === 'all' ? 'All Projects' : `${thematicAreas.find(t => t.key === activeCategory)?.label} Projects`}
                  </motion.h2>
                  
                  <div className="d-flex flex-wrap justify-content-center gap-3 mb-4">
                    <button
                      className={`btn ${activeCategory === 'all' ? 'btn-primary' : 'btn-outline-gray-700'} rounded-pill px-4 border`}
                      onClick={() => setActiveCategory('all')}
                    >
                      All Projects
                    </button>
                    {thematicAreas.map((thematic) => (
                      <button
                        key={thematic.key}
                        className={`btn ${activeCategory === thematic.key ? 'btn-primary' : 'btn-outline-gray-700'} rounded-pill px-4 d-flex align-items-center border`}
                        onClick={() => setActiveCategory(thematic.key)}
                      >
                        <span className="me-2">{thematic.icon}</span>
                        {thematic.label}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Category Description */}
              {activeCategory !== 'all' && (
                <motion.div 
                  className="row justify-content-center mb-5"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="col-lg-10">
                    <div className="bg-white rounded-4 p-5 shadow-sm border">
                      <h3 className="fw-bold text-gray-800 mb-3">
                        {getThematicAreaDescription(activeCategory).title}
                      </h3>
                      <p className="text-gray-600 lead mb-4">
                        {getThematicAreaDescription(activeCategory).description}
                      </p>
                      
                      <div className="row">
                        <div className="col-md-6">
                          <h5 className="fw-semibold text-gray-800 mb-3">Our Approach</h5>
                          <p className="text-gray-600">
                            {getThematicAreaDescription(activeCategory).approach}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <h5 className="fw-semibold text-gray-800 mb-3">Focus Areas</h5>
                          <ul className="text-gray-600">
                            {getThematicAreaDescription(activeCategory).focusAreas.map((area, idx) => (
                              <li key={idx}>{area}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      
                      <div className="mt-4 p-3 bg-gray-100 rounded-3 border">
                        <span className="text-gray-700 fw-semibold">
                          📈 {getThematicAreaDescription(activeCategory).impact}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </section>

          {/* Projects Grid */}
          <section className="py-5 bg-white">
            <div className="container">
              {loading ? (
                <LoadingSpinner text="Loading impactful projects..." />
              ) : (
                <>
                  <div className="row mb-4">
                    <div className="col-12">
                      <p className="text-gray-600 text-center">
                        Showing {filteredProjects.length} project{filteredProjects.length !== 1 ? 's' : ''}
                        {activeCategory !== 'all' && ` in ${thematicAreas.find(t => t.key === activeCategory)?.label}`}
                      </p>
                    </div>
                  </div>

                  <motion.div 
                    className="row g-4"
                    layout
                  >
                    <AnimatePresence mode="wait">
                      {filteredProjects.length > 0 ? (
                        filteredProjects.map((project, index) => (
                          <motion.div
                            key={project.id}
                            className="col-12 col-md-6 col-lg-4"
                            layout
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                          >
                            <ProjectCard 
                              project={project} 
                              featured={project.featured}
                              showDetails={true}
                            />
                          </motion.div>
                        ))
                      ) : (
                        <motion.div 
                          className="col-12 text-center py-5"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.5 }}
                        >
                          <div className="text-gray-600">
                            <div className="fs-1 mb-3">📂</div>
                            <h4 className="fw-semibold text-gray-800 mb-2">No Projects Found</h4>
                            <p className="text-gray-600 mb-4">We're working on new initiatives in this category.</p>
                            <motion.button 
                              className="btn btn-primary me-3"
                              onClick={() => setActiveCategory('all')}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              View All Projects
                            </motion.button>
                            <motion.button 
                              className="btn btn-outline-gray-700 border"
                              onClick={() => setViewMode('thematic')}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              Explore Thematic Areas
                            </motion.button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </>
              )}
            </div>
          </section>
        </>
      )}

      {/* CTA Section */}
      <section className="py-5 position-relative overflow-hidden bg-gray-800">
        <div className="container position-relative z-2">
          <motion.div 
            className="row text-center text-white"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className="col-lg-8 mx-auto">
              <motion.div
                variants={itemVariants}
                className="d-inline-flex align-items-center px-4 py-2 rounded-pill bg-white bg-opacity-10 backdrop-blur mb-4"
              >
                <span className="fw-semibold text-dark">💫 Join Our Mission</span>
              </motion.div>
              
              <motion.h3 
                variants={itemVariants}
                className="display-5 fw-bold mb-4 text-dark"
              >
                Ready to Make an Impact?
              </motion.h3>
              
              <motion.p 
                variants={itemVariants}
                className="lead fs-4 fw-dark text-dark opacity-90 mb-5"
              >
                Join us in creating lasting change and sustainable development across African communities
              </motion.p>
              
              <motion.div 
                variants={containerVariants}
                className="d-flex flex-column flex-sm-row justify-content-center gap-4"
              >
                <Link
                  to="/donate"
                  className="btn btn-light btn-lg px-5 py-3 fw-bold shadow-sm"
                  style={{ borderRadius: '12px' }}
                >
                  💚 Donate Now
                </Link>
                
                <Link
                  to="/volunteer"
                  className="btn btn-outline-dark btn-lg px-5 py-3 fw-bold"
                  style={{ 
                    borderRadius: '12px',
                    border: '2px solid rgba(255,255,255,0.8)',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  🤝 Become a Volunteer
                </Link>
              </motion.div>
              
              <motion.p 
                variants={itemVariants}
                className="text-gray-300 mt-4 small"
              >
                Every contribution helps us expand our reach and deepen our impact
              </motion.p>
            </div>
          </motion.div>
        </div>
      </section>
            {/* YouTube Video Section */}
      <section className="py-5 bg-gray-50">
        <div className="container">
          <motion.div 
            className="row mb-5"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className="col-12 text-center">
              <motion.h2 
                variants={itemVariants}
                className="display-4 fw-black text-gray-800 mb-3"
              >
                See Our Impact in Action
              </motion.h2>
              <motion.p 
                variants={itemVariants}
                className="lead text-gray-600 fs-5 mx-auto"
                style={{ maxWidth: '600px' }}
              >
                Watch how our programs are transforming communities and creating sustainable change across Africa
              </motion.p>
            </div>
          </motion.div>

          <motion.div
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            className="row justify-content-center"
          >
            <div className="col-12 col-lg-10">
              {/* YouTube Video Embed */}
              <div className="ratio ratio-16x9 rounded-4 shadow-lg overflow-hidden">
                <iframe
                  src="https://www.youtube.com/embed/JMsWwHJDaVM"
                  title="Impact Generation Africa - Our Programs in Action"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-100 h-100"
                  style={{ border: 'none' }}
                ></iframe>
              </div>

              {/* Video Description */}
              <motion.div
                variants={itemVariants}
                className="text-center mt-4"
              >
                <p className="text-gray-600 mb-3">
                  See firsthand how our initiatives in food security, climate action, youth empowerment, 
                  and women empowerment are making a difference in communities across Africa.
                </p>
                <div className="d-flex justify-content-center gap-4 flex-wrap">
                  <div className="text-center">
                    <div className="fw-bold text-gray-800">50K+</div>
                    <div className="text-gray-600 small">Lives Impacted</div>
                  </div>
                  <div className="text-center">
                    <div className="fw-bold text-gray-800">100+</div>
                    <div className="text-gray-600 small">Communities Reached</div>
                  </div>
                  <div className="text-center">
                    <div className="fw-bold text-gray-800">25K+</div>
                    <div className="text-gray-600 small">Trees Planted</div>
                  </div>
                  <div className="text-center">
                    <div className="fw-bold text-gray-800">5K+</div>
                    <div className="text-gray-600 small">Youth Empowered</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <style>{`
        .object-fit-cover {
          object-fit: cover;
        }
        
        .backdrop-blur {
          backdrop-filter: blur(10px);
        }

        /* Gradient classes for thematic areas */
        .from-gray-500.to-gray-700 { background: linear-gradient(135deg, #6b7280 0%, #374151 100%); }
        .from-gray-600.to-gray-800 { background: linear-gradient(135deg, #4b5563 0%, #1f2937 100%); }
        .from-gray-400.to-gray-600 { background: linear-gradient(135deg, #9ca3af 0%, #4b5563 100%); }
        
        /* Custom button styles */
        .btn-primary {
          background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
          border: none;
          color: white;
        }
        
        .btn-outline-gray-700 {
          border-color: #4a5568;
          color: #4a5568;
          background: transparent;
        }
        
        .btn-outline-gray-700:hover {
          background: #4a5568;
          color: white;
        }
        
        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </>
  );
};

export default Programs;